import React, { PropsWithChildren } from 'react';
import { useNavigate } from 'react-router';

import { ArrowUpRightFromSquare } from '../icons/ArrowUpRightFromSquare';
import {
  CardActionText,
  CardContentWrapper,
  CardIconContainer,
  CardMainBody,
  CardTitle,
  CardTitleWrapper,
  IconColor,
} from './Card.styles';

export type Icon = {
  element: JSX.Element;
  color?: IconColor;
};

interface CardProps {
  title: string;
  url?: string;
  route?: string;
  navState?: Record<string, any>;
  newTab?: boolean;
  description: string;
  height?: string;
  width?: string;
  icon?: Icon;
  hasHeaderIcons?: boolean;
  statusPill?: JSX.Element;
  pillPosition?: PillPosition;
  includeUrlArrow?: boolean;
  className?: string;
  dataTestId?: string;
  onClick?: () => void;
}

export enum PillPosition {
  BESIDE,
  BELOW,
}

export function Card({
  title,
  url,
  route,
  navState,
  children,
  icon,
  statusPill,
  pillPosition,
  height,
  width,
  actionText,
  actionIcon,
  newTab = true,
  hasHeaderIcons = true,
  includeUrlArrow = true,
  className,
  dataTestId,
  onClick,
}: PropsWithChildren<Omit<CardProps, 'description'> & { actionText?: string; actionIcon?: Icon }>) {
  const navigate = useNavigate();

  const testId = dataTestId || `${title.split(' ').join('-').toLowerCase()}-card`;

  const handleClick = () => {
    if (route) {
      navigate(route, { state: navState });
      return;
    }

    if (url && newTab) {
      window.open(url, '_blank');
    } else if (url) {
      window.location.href = url;
    }

    onClick?.();
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleClick();
    }
  };

  const cardRole = route || url || onClick ? 'button' : 'none';
  const enableInteractiveStyles = Boolean(url) || Boolean(route) || Boolean(onClick);

  return (
    <CardMainBody
      data-testid={testId}
      role={cardRole}
      tabIndex={0}
      $cardHeight={height}
      $cardWidth={width}
      $iconColor={icon ? icon.color : undefined}
      onClick={handleClick}
      onKeyDown={handleKeyDown}
      // allows importing/direct styling via styled-components
      className={className}
      $enableInteractiveStyles={enableInteractiveStyles}
    >
      <CardContentWrapper>
        {(title || statusPill || (url && hasHeaderIcons && includeUrlArrow)) && (
          <CardTitleWrapper>
            <CardTitle as="h2">
              {icon && hasHeaderIcons && <CardIconContainer>{icon.element}</CardIconContainer>}
              {title}
              {url && hasHeaderIcons && includeUrlArrow && <ArrowUpRightFromSquare />}
              {pillPosition === PillPosition.BESIDE && statusPill}
            </CardTitle>
            {pillPosition === PillPosition.BELOW && statusPill}
          </CardTitleWrapper>
        )}
        {children}
      </CardContentWrapper>
      {actionText && (
        <CardActionText>
          {actionIcon?.element} {actionText}
        </CardActionText>
      )}
    </CardMainBody>
  );
}

export default Card;
